/**
 * src/pages/venue/ApproverDetailsModal.tsx — 2004va
 *
 * Modal shown when picker taps "Request approval" on a shot. Collects:
 *   - Approver name        (e.g. "Purple Wall Music")
 *   - Approver contact     (email or phone)
 *   - Relationship         (artist_management / subject_self / etc)
 *   - Channel              (email / whatsapp / sms)
 *
 * On submit: calls createApprovalRequest which:
 *   - Generates a secure token
 *   - Inserts into approval_requests
 *   - Marks the pick as needs_approval
 *   - Logs the decision
 *
 * Future: actually send the message via the notify edge function.
 * For demo: we show the approver URL in a 'copied to clipboard' toast
 * so the picker can forward it manually.
 */

import { useState, type FormEvent } from 'react'
import { Mail, MessageCircle, Phone, X } from 'lucide-react'
import { createApprovalRequest } from '@/lib/venue'
import { cn } from '@/components/ui/utils'
import type { VenuePick, ApproverRelationship, ApprovalChannel } from '@/types'
import type { PickerSession } from '@/lib/venue'

interface ApproverDetailsModalProps {
  pick:       VenuePick
  picker:     PickerSession
  onClose:    () => void
  onComplete: (approverUrl: string) => void
}

const RELATIONSHIPS: Array<{ value: ApproverRelationship; label: string; hint: string }> = [
  { value: 'artist_management', label: 'Artist management',  hint: 'Agent or touring manager' },
  { value: 'artist_self',       label: 'Artist directly',    hint: 'The act themselves' },
  { value: 'venue_marketing',   label: 'Venue marketing',    hint: 'Club PR or brand team' },
  { value: 'subject_self',      label: 'Subject directly',   hint: 'The identifiable person' },
  { value: 'legal',             label: 'Legal',              hint: 'Legal review required' },
  { value: 'other',             label: 'Other',              hint: 'Something else' },
]

export function ApproverDetailsModal({ pick, picker, onClose, onComplete }: ApproverDetailsModalProps) {
  const [name, setName]                 = useState('')
  const [contact, setContact]           = useState('')
  const [relationship, setRelationship] = useState<ApproverRelationship>('artist_management')
  const [channel, setChannel]           = useState<ApprovalChannel>('email')
  const [submitting, setSubmitting]     = useState(false)
  const [error, setError]               = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (!name.trim() || !contact.trim()) {
      setError('Name and contact are both required.')
      return
    }

    setSubmitting(true)
    try {
      const req = await createApprovalRequest({
        pick_id:              pick.id,
        approver_name:        name.trim(),
        approver_contact:     contact.trim(),
        approver_relationship: relationship,
        channel,
        picker_id:            picker.picker_id,
      })

      const url = `${window.location.origin}/approve/${req.access_token}`
      onComplete(url)
      onClose()
    } catch (err) {
      console.error('createApprovalRequest failed:', err)
      setError(err instanceof Error ? err.message : 'Request failed. Try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80  animate-fade-in" />
      <div
        className="absolute bottom-0 inset-x-0 bg-room-bg border-t border-room-border rounded-t-3xl p-5 pb-8 max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={e => e.stopPropagation()}
      >

        {/* Handle */}
        <div className="w-12 h-1 rounded-full bg-room-muted/40 mx-auto mb-5" />

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-room-warning font-bold">
              Request approval
            </p>
            <h2 className="text-xl font-bold text-white mt-1">Who approves this shot?</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-room-muted hover:text-white hover:bg-room-surface transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Context hint */}
        <div className="px-5 pb-2">
          <p className="text-white/40 text-xs">Fill in both fields below to enable sending</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">

          {/* Name */}
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-1.5">
              Approver name <span className="text-room-warning">*</span>
            </span>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={submitting}
              placeholder="e.g. Purple Wall Music"
              className="w-full bg-room-surface border border-room-border rounded-xl px-4 py-3 text-base text-white placeholder:text-room-muted focus:outline-none focus:border-room-gold/60 disabled:opacity-50"
            />
          </label>

          {/* Relationship — visual picker */}
          <div>
            <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-2">
              Relationship
            </span>
            <div className="grid grid-cols-2 gap-2">
              {RELATIONSHIPS.map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRelationship(r.value)}
                  disabled={submitting}
                  className={cn(
                    'text-left p-3 rounded-xl border transition-colors',
                    relationship === r.value
                      ? 'bg-room-gold/15 border-room-gold/60 text-white'
                      : 'bg-room-surface border-room-border text-white/80 hover:border-room-muted/40'
                  )}
                >
                  <p className="text-sm font-semibold leading-tight">{r.label}</p>
                  <p className="text-[10px] text-room-muted mt-0.5 leading-snug">{r.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Channel */}
          <div>
            <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-2">
              Send via
            </span>
            <div className="grid grid-cols-3 gap-2">
              <ChannelTile icon={Mail}          label="Email"     active={channel === 'email'}    onClick={() => setChannel('email')}    />
              <ChannelTile icon={MessageCircle} label="WhatsApp"  active={channel === 'whatsapp'} onClick={() => setChannel('whatsapp')} />
              <ChannelTile icon={Phone}         label="SMS"       active={channel === 'sms'}      onClick={() => setChannel('sms')}      />
            </div>
          </div>

          {/* Contact — field adapts to channel */}
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-room-muted font-semibold mb-1.5">
              {channel === 'email' ? 'Email' : 'Phone / WhatsApp number'} *
            </span>
            <input
              type={channel === 'email' ? 'email' : 'tel'}
              value={contact}
              onChange={e => setContact(e.target.value)}
              disabled={submitting}
              autoComplete={channel === 'email' ? 'email' : 'tel'}
              placeholder={channel === 'email' ? 'Enter email address' : 'Enter phone number'}
              className="w-full bg-room-surface border border-room-border rounded-xl px-4 py-3 text-base text-white placeholder:text-room-muted focus:outline-none focus:border-room-gold/60 disabled:opacity-50"
            />
          </label>

          {/* Error */}
          {error && (
            <div className="bg-room-error/15 border border-room-error/40 rounded-xl p-3.5 text-sm text-room-error">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || !name.trim() || !contact.trim()}
            className="w-full py-4 rounded-2xl font-bold text-base bg-room-warning text-black hover:bg-room-warning/90 transition-all min-h-[48px] disabled:bg-room-surface disabled:text-room-muted disabled:cursor-not-allowed disabled:border disabled:border-room-border"
          >
            {submitting ? 'Sending…' : 'Send approval request'}
          </button>

          {/* Note */}
          <p className="text-[11px] text-room-muted text-center leading-relaxed">
            A time-limited link will be generated (valid 72 hours). In this
            demo the link is shown on screen to copy manually — in production
            it would be sent via the chosen channel.
          </p>

        </form>
      </div>
    </div>
  )
}

// ── Channel picker tile ───────────────────────────────────────────────────────

interface ChannelTileProps {
  icon:    typeof Mail
  label:   string
  active:  boolean
  onClick: () => void
}

function ChannelTile({ icon: Icon, label, active, onClick }: ChannelTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'py-3 rounded-xl border transition-colors',
        'flex flex-col items-center gap-1 min-h-[48px]',
        active
          ? 'bg-room-gold/15 border-room-gold/60 text-white'
          : 'bg-room-surface border-room-border text-white/80 hover:border-room-muted/40'
      )}
    >
      <Icon size={16} strokeWidth={active ? 2.5 : 1.8} />
      <span className="text-[11px] font-semibold">{label}</span>
    </button>
  )
}
